import { InfoOutlined } from 'components/icons'
import { useEditMode } from 'purchasers/editMode'
import { omit, path } from 'ramda'
import React, { cloneElement } from 'react'
import { SizableText, XStack, YStack } from 'tamagui'
import Checkbox from './checkbox'
import { Label } from './label'

interface MultiCheckboxProps {
    options: readonly string[] | string[] // Array of checkbox options
    value?: string[] // Selected values
    onChange?: (values: string[]) => void // Callback with updated array
    label?: string // Field label
    renderNewLines?: boolean // Line breaks vs comma-separated (default: false)
    disabled?: boolean // Disable all checkboxes
    error?: boolean | any // Error state
}

const MultiCheckbox = (props: MultiCheckboxProps) => {
    const { editMode } = useEditMode()
    const { options, value, onChange, label, renderNewLines, disabled } = props

    // Read-only mode: display selected values as comma-separated text
    if (!editMode) {
        const selectedValues = value && value.length > 0 ? value : []

        return (
            <YStack gap="$2">
                {label && <Label>{label}</Label>}
                {renderNewLines ? (
                    // Line-separated display
                    selectedValues.length > 0 ? (
                        selectedValues.map((v, i) => (
                            <React.Fragment key={i}>
                                <SizableText>{v}</SizableText>
                                {i < selectedValues.length - 1 && <br />}
                            </React.Fragment>
                        ))
                    ) : (
                        <SizableText>{'\u00A0'}</SizableText>
                    )
                ) : (
                    // Comma-separated display
                    <SizableText>{selectedValues.length > 0 ? selectedValues.join(', ') : '\u00A0'}</SizableText>
                )}
            </YStack>
        )
    }

    // Edit mode: render individual checkboxes
    return (
        <YStack gap="$2">
            {label && <Label>{label}</Label>}
            <XStack gap="$4" flexWrap="wrap">
                {options.map(option => {
                    const isChecked = value?.includes(option) ?? false
                    return (
                        <Checkbox
                            key={option}
                            label={option}
                            checked={isChecked}
                            disabled={disabled}
                            onChange={e => {
                                const checked = e.target.checked
                                const current = value || []
                                const newValue = checked
                                    ? [...current, option]
                                    : current.filter(v => v !== option)
                                onChange?.(newValue)
                            }}
                        />
                    )
                })}
            </XStack>
        </YStack>
    )
}

export default MultiCheckbox

interface SlotMultiCheckboxProps
    extends Omit<MultiCheckboxProps, 'error' | 'value' | 'onChange'> {
    field: any
    formState: any
    formControlProps?: any
    helperText?: string
}

export const SlotMultiCheckbox = ({
    field,
    formState,
    helperText,
    formControlProps,
    ...props
}: SlotMultiCheckboxProps) => {
    const { errors } = formState
    const error = path(field.name.split('.'), errors) as any
    const { ref, ...fieldProps } = field

    return (
        <YStack gap="$2" {...formControlProps}>
            {helperText && !error?.message && (
                <SizableText size="$2" color="$gray10">
                    {helperText}
                </SizableText>
            )}
            <MultiCheckbox
                {...props}
                value={field.value}
                onChange={values => field.onChange(values)}
                error={error}
            />
            {error?.message && (
                <XStack alignItems="center" gap="$1">
                    <InfoOutlined size={14} />
                    <SizableText size="$2" color="$red10">
                        {error.message}
                    </SizableText>
                </XStack>
            )}
        </YStack>
    )
}
